import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import * as moment_ from 'moment';
const moment = moment_;

import { BilletAutomaticErrorDTO } from '@gipi-billet/bank-slip/models/dto/billet-automatic-error.dto';
import { FinancialBilletFinancialService } from '@gipi-billet/bank-slip/services/billet-financial.service';
import { FinancialBankAccountSelectDTO } from '@gipi-financial/bank-account/models/dto/bank-account-select.dto';
import { FinancialPeriodicity, FinancialPeriodicityEnum } from '@gipi-financial/bill/enums/periodicity.enum';
import { FinancialBillInstallment } from '@gipi-financial/bill/models/bill-installment.model';
import { FinancialBill } from '@gipi-financial/bill/models/bill.model';
import { FinancialBillService } from '@gipi-financial/bill/services/bill.service';
import { CustomMessageService } from '@gipi-shared/services/custom-message.service';
import { BankCodeUtil, BankCodes } from '@gipi-shared/utils/bank-codes-billet.util';
import { PdfUtil } from '@gipi-shared/utils/pdf.util';
import { APP_MESSAGES, AbstractComponent, ConfirmationService, DateUtil, INJECTOR, MonthYear, NumberUtil, ObjectUtil, StringUtil } from '@gipisistemas/ng-core';

@Component({
    templateUrl: './recurring-release-dialog.component.html',
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            height: 100%;
        }
    `]
})
export class RecurringReleaseDialogComponent extends AbstractComponent implements OnInit {

    installment: FinancialBillInstallment;

    installmentList: FinancialBillInstallment[] = [];

    periodicity: FinancialPeriodicity = 'MONTHLY';

    periodicityDay: number;

    periodicityEnum: typeof FinancialPeriodicityEnum = FinancialPeriodicityEnum;

    dayList: { id: number, description: string }[] = [];

    day: { id: number, description: string };

    begin: MonthYear = null;

    end: MonthYear = null;

    initialDueDate: Date = null;

    selectPeriod: boolean = false;

    numberInstallments: number;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: FinancialBillInstallment,
        public dialogRef: MatDialogRef<RecurringReleaseDialogComponent>,
        private _billService: FinancialBillService,
        private _confirmationService: ConfirmationService,
        private _billetFinancialService: FinancialBilletFinancialService,
        messageService: CustomMessageService,
        router: Router,
        activatedRoute: ActivatedRoute
    ) {
        super(messageService, router, activatedRoute);
        this._generateDayList();
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.installment = this.data;

        setTimeout(() => {
            this.selectionChangePeriodicity();
        });
    }

    private _generateDayList(): void {
        this.dayList = Array.from({ length: 31 }, (_, index) => ({
            id: index + 1,
            description: ('00' + (index + 1)).slice(-2)
        }));
    }

    selectionChangePeriodicity(): void {
        const date: Date = new Date(moment(this.installment.dueDate).format('yyyy/MM/DD'));
        this.begin = new MonthYear(date.getMonth(), date.getFullYear());
        this.initialDueDate = new Date(date);

        this.selectPeriod = false;
    }

    modelChangeSelectPeriod(): void {
        const date: Date = new Date(moment(this.installment.dueDate).format('yyyy/MM/DD'));
        this.day = this.dayList.find(day => day.id === date.getDate());
        this.begin = new MonthYear(date.getMonth(), date.getFullYear());
        this.initialDueDate = new Date(date);

        this.numberInstallments = 1;
        this.periodicityDay = 1;
    }

    async save(): Promise<void> {
        try {
            this.loading = true;

            const bankAccount: FinancialBankAccountSelectDTO = FinancialBankAccountSelectDTO.transformToDTO(this.installment.bankAccount);
            if ((this.installment.bill.type === 'RECEIVABLE') && (this.installment.chargeType.type === 'BANK_SLIP')) {
                if (bankAccount.type !== 'CHECKING_ACCOUNT') {
                    this.addWarningMessage('Não é possível gerar boleto usando uma conta bancária que não seja do tipo "Conta corrente"');
                    this.loading = false;
                    return;
                }

                const allowedBankAccount: boolean = BankCodeUtil.isValidBankCode((bankAccount.bankCode as BankCodes));
                if (!allowedBankAccount) {
                    this.loading = false;
                    this.loadingChange.emit(this.loading);
                    this.addWarningMessage('Não é possível gerar boleto usando uma conta bancária que não seja válida');
                    return;
                }
            }

            this._generateInstallments();

            const bill: FinancialBill = this.billInstallListToBill(this.installmentList);

            await this._billService.save(bill).toPromise().then(b => {
                this.addSuccessMessage(INJECTOR.get(APP_MESSAGES).SUCCESS);
                if ((this.installment.bill.type === 'RECEIVABLE') && (this.installment.chargeType.type === 'BANK_SLIP')) {
                    this.confirmGenerateBankSlip(b);
                } else {
                    this.close(true);
                }
            }, error => {
                this.loading = false;
                throw new Error(error);
            });
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    billInstallListToBill(billInstallmentList: FinancialBillInstallment[]): FinancialBill {
        const billInstallmentListClone: FinancialBillInstallment[] = [];

        for (let i: number = 0; i < billInstallmentList.length; i++) {
            const billInstalment: FinancialBillInstallment = ObjectUtil.clone(billInstallmentList[i]);
            billInstalment.bill = null;
            billInstallmentListClone.push(billInstalment);
        }

        const bill: FinancialBill = ObjectUtil.clone(billInstallmentList[0].bill);
        bill.billInstallmentList = billInstallmentListClone;

        return bill;
    }

    close(navigate: boolean): void {
        this.dialogRef.close(navigate);
    }

    private _validate(): void {
        if (!NumberUtil.isPositive(this.installment.amount)) {
            throw new Error('Valor é obrigatório e não foi informado');
        } else if (ObjectUtil.isNull(this.periodicity)) {
            throw new Error('Periodicidade é obrigatório e não foi informado');
        } else if ((this.periodicity.toString() !== 'MONTHLY') && (ObjectUtil.isNull(this.numberInstallments) || (Number(this.numberInstallments) <= 0))) {
            throw new Error('Qtd. de parcelas é obrigatório e não foi informado');
        } else if ((this.periodicity.toString() !== 'MONTHLY') && DateUtil.isLessThan(new Date(this.initialDueDate), new Date(this.installment.issuanceDate))) {
            throw new Error('Data de vencimento não pode ser menor que a data de emissão');
        } else if (this.periodicity.toString() === 'FREE' && (ObjectUtil.isNull(this.periodicityDay) || (Number(this.periodicityDay) <= 0))) {
            throw new Error('Qtd. de dias entre parcelas é obrigatório e não foi informado');
        } else if ((this.periodicity.toString() === 'MONTHLY') && (ObjectUtil.isNull(this.numberInstallments) || (Number(this.numberInstallments) <= 0))) {
            throw new Error('Qtd. de parcelas é obrigatório e não foi informado');
        } else if (this.periodicity.toString() === 'MONTHLY' && this.selectPeriod) {
            const issuanceDate: Date = new Date(moment(this.installment.issuanceDate).format('yyyy/MM/DD'));
            const dateBegin: Date = moment().day(this.day.id).month(this.begin.month).year(this.begin.year).toDate();
            const dateEnd: Date = moment().day(this.day.id).month(this.end.month).year(this.end.year).toDate();
            const lastDayOfMonth: number = DateUtil.getLastDayMonth(this.begin.month);

            if (ObjectUtil.isNull(this.day)) {
                throw new Error('Dia do vencimento é obrigatório e não foi informado');
            } else if (ObjectUtil.isNull(this.begin)) {
                throw new Error('Mês/Ano inicial é obrigatório e não foi informado');
            } else if (ObjectUtil.isNull(this.end)) {
                throw new Error('Mês/Ano final é obrigatório e não foi informado');
            } else if (DateUtil.isLessThanMonth(dateEnd, dateBegin)) {
                throw new Error('Mês/Ano final não pode ser menor que Mês/Ano inicial');
            } else if (DateUtil.isLessThan(new Date(dateBegin), new Date(issuanceDate))) {
                throw new Error('Mês/Ano inicial não pode ser menor que a Data emissão');
            } else if (this.day.id > lastDayOfMonth) {
                throw new Error(`Mês/Ano inicial possui ${lastDayOfMonth} dias. Portanto, o dia do vencimento é inválido`);
            }
        }
    }

    private _generateInstallments(): void {
        try {
            this.loading = true;
            this._validate();

            const dateList: Date[] = this._getDateList();
            const description: string = StringUtil.isEmpty(this.installment.description) ? '' : this.installment.description;

            const installmentList: FinancialBillInstallment[] = dateList.map((date, index) => {
                const installment: FinancialBillInstallment = ObjectUtil.clone(this.installment);
                installment.id = null;
                installment.saleBillInstallmentId = null;
                installment.bill.id = null;
                installment.bill.saleBillId = null;
                installment.documentNumber = null;
                installment.bill.periodicity = this.periodicity;
                installment.portion = dateList.indexOf(date) + 1;
                installment.description = description;

                installment.issuanceDate = new Date(installment.issuanceDate);
                installment.issuanceDate.setMonth(installment.issuanceDate.getMonth() + index);

                installment.dueDate = date;
                installment.ourNumber = null;

                return installment;
            });

            this.installmentList = installmentList;
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

    private _getDateList(): Date[] {
        const dateList: Date[] = [];
        const base = new Date(this.initialDueDate);

        if (this.selectPeriod) {
            // cálculo via intervalo de meses (begin → end)
            const startYear = this.begin.year;
            const startMonth = this.begin.month;
            const endYear = this.end.year;
            const endMonth = this.end.month;
            const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth);

            const baseDate = new Date(startYear, startMonth, this.day.id);

            for (let i = 0; i <= monthsDiff; i++) {
                dateList.push(this._getNextDueDate(baseDate, i, this.periodicity, this.periodicityDay));
            }
        } else {
            for (let i = 0; i < Number(this.numberInstallments); i++) {
                dateList.push(this._getNextDueDate(base, i, this.periodicity, this.periodicityDay));
            }
        }

        return dateList;
    }

    /**
     * Calcula a próxima data de vencimento de acordo com a periodicidade.
     * - Para WEEKLY/ FREE: soma dias direto
     * - Para MONTHLY, BIMONTHLY, QUARTERLY, SEMESTER, YEARLY: usa cálculo de meses
     *   com fallback para "empurrar" para o próximo dia quando o mês não tem o dia
     */
    private _getNextDueDate(baseDate: Date, step: number, periodicity: FinancialPeriodicity, periodicityDay?: number): Date {
        const baseDay = baseDate.getDate();
        const baseMonth = baseDate.getMonth();
        const baseYear = baseDate.getFullYear();

        switch (periodicity.toString()) {
            case 'WEEKLY':
                return moment(baseDate).add(step * 7, 'days').toDate();

            case 'FREE':
                return moment(baseDate).add(step * (periodicityDay || 0), 'days').toDate();

            case 'MONTHLY':
                return this._addMonthsKeepingDay(baseYear, baseMonth, baseDay, step);

            case 'BIMONTHLY':
                return this._addMonthsKeepingDay(baseYear, baseMonth, baseDay, step * 2);

            case 'QUARTERLY':
                return this._addMonthsKeepingDay(baseYear, baseMonth, baseDay, step * 3);

            case 'SEMESTER':
                return this._addMonthsKeepingDay(baseYear, baseMonth, baseDay, step * 6);

            case 'YEARLY':
                return this._addMonthsKeepingDay(baseYear, baseMonth, baseDay, step * 12);

            default:
                return new Date(baseDate);
        }
    }

    /**
     * Adiciona meses e mantém o dia escolhido.
     * Se o mês não tiver esse dia, empurra para o próximo dia válido (ex.: 30/01 → 01/03).
     */
    private _addMonthsKeepingDay(year: number, month: number, day: number, offsetMonths: number): Date {
        const targetMonthIndex = month + offsetMonths;
        const targetYear = year + Math.floor(targetMonthIndex / 12);
        const targetMonth = ((targetMonthIndex % 12) + 12) % 12;

        const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();

        if (day <= lastDay) {
            return new Date(targetYear, targetMonth, day);
        }

        // se não existe esse dia, empurra para depois do último dia do mês
        return new Date(targetYear, targetMonth, lastDay + (day - lastDay));
    }

    async confirmGenerateBankSlip(bill: FinancialBill): Promise<void> {
        const isConfirmed: boolean = await this._confirmationService.confirm({
            title: 'Confirmação',
            message: 'Deseja imprimir o boleto?',
        });

        this._generateBankSlip(bill.billInstallmentList, isConfirmed);
    }

    private _generateBankSlip(installmentList: FinancialBillInstallment[], generatePdf: boolean): void {
        try {
            this.loading = true;
            this._billetFinancialService.generateBilletAll(installmentList, generatePdf).toPromise().then(async result => {
                if (result.isPdf) {
                    if (generatePdf) {
                        const fileURL = URL.createObjectURL(result.resp as Blob);
                        if (!StringUtil.isEmpty(fileURL)) {
                            PdfUtil.print(fileURL);
                        }
                    }
                } else {
                    await this._billetFinancialService.sendOrPrintBillet([result.resp as BilletAutomaticErrorDTO], generatePdf, 0);
                }

                this.loading = false;
                this.close(true);
            }).catch(error => {
                this.loading = false;
                this.addErrorMessage(error);
            });
        } catch (e) {
            this.loading = false;
            this.handleError(e);
        }
    }

}
